import { Box, Container, CssBaseline, Grid } from "@mui/material";
import { Applications } from "configs";
import { useShouldRenderPageGuard } from "hooks/useShouldRenderPageGuard";
import { appDefaultRoute, appPrefix } from "pages/app-taxi-ranking";
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useMatch } from "react-router-dom";
import { setRootPath } from "reducers/auth.reducer";
import { useAppDispatch } from "store";
import LoadingLayout from "layouts/common/loader/LoadingLayout.tsx";
import { clearLocations, setLocations } from "reducers/newLocation.reducer";
import { clearDevices, setDeviceList } from "reducers/devices.reducer";
import { newExecuteRetrieveLocationsRequest } from "api/common/side-panel.api";
import { executeGetAllDevicesRequest } from "api/app-taxi-ranking/taxi-video-requests.api";
import { useSelector } from "react-redux";
import { LoadingWrapper } from "components/wrappers/LoadingWrapper";
import NavbarTaxiRank from "./header/NavbarTaxiRank";

const { AppIdTaxiRanking } = Applications;

export default function DeviceMonitorLayout(): JSX.Element {
    const match = useMatch(appPrefix);
    const { shouldRender } = useShouldRenderPageGuard(AppIdTaxiRanking);
    const { locationsTree } = useSelector((state) => state.newLocation);
    const dispatch = useAppDispatch();
    const locationsAbortControllerRef = useRef<AbortController | null>(null);
    const devicesAbortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        dispatch(setRootPath("/taxi-ranking/home"));
    }, []);

    useEffect(() => {
        (async () => {
            dispatch(clearLocations());
            dispatch(clearDevices());
            await Promise.all([getLocations(), getDevices()]);
        })();

        return () => {
            locationsAbortControllerRef.current?.abort();
            devicesAbortControllerRef.current?.abort();
        };
    }, []);

    const getLocations = async () => {
        try {
            locationsAbortControllerRef.current = new AbortController();
            const locationSignal = locationsAbortControllerRef.current.signal;

            const locationsResponse = await newExecuteRetrieveLocationsRequest({
                signal: locationSignal,
            });

            if (locationsResponse && Array.isArray(locationsResponse)) {
                dispatch(setLocations(locationsResponse));
            }
        } catch (e) {
            console.log(e);
        }
    };

    const getDevices = async () => {
        try {
            devicesAbortControllerRef.current = new AbortController();
            const { signal } = devicesAbortControllerRef.current;

            const response = await executeGetAllDevicesRequest(
                {},
                {
                    signal,
                    disableNotification: true,
                },
            );

            if (response && Array.isArray(response)) {
                dispatch(setDeviceList(response));
            }
        } catch (e) {
            console.log(e);
        }
    };

    if (match) return <Navigate to={appDefaultRoute} replace />;

    return shouldRender ? (
        <Container maxWidth="xl" disableGutters>
            <CssBaseline />
            <NavbarTaxiRank />
            <Grid className="custom-container">
                {locationsTree.length > 0 ? (
                    <Box
                        sx={{
                            position: "relative",
                            height: "100%",
                            transition: "all 0.5s ease",
                            padding: "25px",
                            boxSizing: "border-box",
                        }}
                    >
                        <Outlet />
                    </Box>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                        <LoadingWrapper isLoading />
                    </Box>
                )}
            </Grid>
        </Container>
    ) : (
        <LoadingLayout />
    );
}
